import {
  Bell,
  CircleHelp,
  ChevronDown,
  Search,
  ShieldAlert,
} from "lucide-react";
import type { ReactNode } from "react";
import { AppLink, StatusBadge } from "@league-command/ui";
import { AccountMenu } from "../features/auth/AccountMenu";
import { EnvironmentBanner } from "./EnvironmentBanner";
import {
  commissionerNavigation,
  dominantHrefForLabStage,
  stageDrivenMobileNavigation,
  stageDrivenPrimaryNavigation,
  type LabStageId,
} from "./navigation";

interface AppShellProps {
  children: ReactNode;
  path: string;
  labStage?: LabStageId | null;
}

const isCurrent = (
  path: string,
  href: string,
  navigation: readonly { href: string }[],
): boolean => {
  const mostSpecificMatch = navigation.reduce<string | null>(
    (current, item) => {
      const matches =
        path === item.href || path.startsWith(`${item.href}/`);
      if (!matches || (current && current.length >= item.href.length)) {
        return current;
      }
      return item.href;
    },
    null,
  );

  return mostSpecificMatch === href;
};

export const AppShell = ({ children, path, labStage = null }: AppShellProps) => {
  const primaryItems = stageDrivenPrimaryNavigation(labStage);
  const mobileItems = stageDrivenMobileNavigation(labStage);
  const dominantHref =
    labStage === null ? null : dominantHrefForLabStage(labStage);

  return (
  <div className="app-shell">
    <aside className="side-nav" aria-label="Primary navigation">
      <AppLink className="wordmark" href="/league" aria-label="League Command home">
        League Command
      </AppLink>

      <nav className="side-nav__links">
        {primaryItems.map((item) => {
          const Icon = item.icon;
          const dominant = dominantHref === item.href;
          return (
            <AppLink
              key={item.href}
              href={item.href}
              current={isCurrent(path, item.href, primaryItems)}
              className="side-nav__link"
              data-dominant={dominant || undefined}
            >
              <Icon aria-hidden size={21} strokeWidth={1.8} />
              <span>{item.label}</span>
              {dominant ? <small>Now</small> : null}
            </AppLink>
          );
        })}
      </nav>

      <nav className="side-nav__links side-nav__links--commissioner" aria-label="Commissioner">
        {commissionerNavigation.map((item) => {
          const Icon = item.icon;
          return (
            <AppLink
              key={item.href}
              href={item.href}
              current={isCurrent(path, item.href, commissionerNavigation)}
              className="side-nav__link"
            >
              <Icon aria-hidden size={20} strokeWidth={1.8} />
              <span>{item.label}</span>
            </AppLink>
          );
        })}
      </nav>

      <div className="system-rail">
        <div className="system-rail__item">
          <StatusBadge tone="warning">Local canonical lab</StatusBadge>
          <span>Canonical commands persist only to the synthetic local league.</span>
        </div>
        <div className="system-rail__item system-rail__item--warning">
          <StatusBadge tone="warning">Synthetic inputs</StatusBadge>
          <span>No production or live MFL data is used.</span>
        </div>
      </div>
    </aside>

    <header className="top-bar">
      <button className="tenant-select" type="button" aria-label="Select league">
        <span className="tenant-mark">N</span>
        <span>
          <strong>Synthetic lab league</strong>
          <small>2026 season</small>
        </span>
        <ChevronDown aria-hidden size={16} />
      </button>

      <div className="fixture-state">
        <span className="fixture-state__dot" aria-hidden />
        <span>
          <strong>Synthetic local league</strong>
          <small>Canonical local authority · no production access</small>
        </span>
      </div>

      <div className="top-bar__tools" aria-label="Account tools">
        <button type="button" aria-label="Search">
          <Search aria-hidden size={20} />
        </button>
        <button type="button" aria-label="Notifications">
          <Bell aria-hidden size={20} />
        </button>
        <button type="button" aria-label="Help">
          <CircleHelp aria-hidden size={20} />
        </button>
        <span className="top-bar__divider" aria-hidden />
        <AccountMenu path={path} />
      </div>
    </header>

    <main id="main-content" className="main-content">
      <EnvironmentBanner />
      <div className="proof-warning" role="note">
        <ShieldAlert aria-hidden size={16} />
        <span>
          Canonical commits persist inside this synthetic local league; production
          and live MFL remain untouched.
        </span>
      </div>
      {children}
    </main>

    <nav className="mobile-nav" aria-label="Mobile navigation">
      {mobileItems.map((item) => {
        const Icon = item.icon;
        const dominant = dominantHref === item.href;
        return (
          <AppLink
            key={`${item.href}-${item.label}`}
            href={item.href}
            current={isCurrent(path, item.href, mobileItems)}
            className="mobile-nav__link"
            data-dominant={dominant || undefined}
          >
            <Icon aria-hidden size={20} />
            <span>{item.label}</span>
          </AppLink>
        );
      })}
    </nav>
  </div>
  );
};
